/**
 * The one place a catalogue item's configuration is assembled for the backend.
 *
 * Three call sites built this independently — the price preview, send-estimate,
 * and zoom-out — and the preview built it *differently*: it sent only
 * `state.values`, omitting `state.custom` (free-text and custom-dimension
 * fields) and `state.multi` (multi-select fields). The other two merged all
 * three.
 *
 * So the price a rep was shown was computed against a different specification
 * from the one submitted. Whenever a custom or multi-select field affects cost,
 * the previewed price was for a spec the customer never receives — which is the
 * same class of defect as the displayed-vs-emailed divergence, arriving one
 * layer earlier and invisible to a test that compares the two endpoints, since
 * both would faithfully report prices for inputs that already disagreed.
 *
 * Keeping it here rather than in the page is the `frontend-architecture.md`
 * rule: non-visual logic extracted during journey work lands in shared-core. It
 * is also what makes the shape testable without rendering anything.
 */

import type { CatalogueField } from "../data/catalogue-types";

/**
 * The chip value that means "the orderer picked 'Other…' for a
 * choice_or_custom field" rather than any real preset code. The field's real
 * value lives in `custom[key]` once this is selected — see
 * `buildCatalogueConfiguration`'s precedence. Shared between the page (which
 * renders the chip) and `catalogue-custom-value.ts` (which decides whether a
 * field currently holds an active custom value), so the two cannot drift.
 */
export const CUSTOM_OPTION_SENTINEL = "__custom__";

/** The parts of the item-page config state that describe the product. */
export interface CatalogueConfigurationState {
  /** Single-choice field selections, keyed by field key. */
  values: Record<string, string>;
  /** Free-text and custom-dimension entries, keyed by field key. */
  custom: Record<string, string>;
  /** Multi-select selections, keyed by field key. */
  multi: Record<string, string[]>;
}

/**
 * Flatten the configuration state into the `Record<string, string>` every
 * catalogue endpoint expects.
 *
 * Multi-select values are joined with ", " — the format the backend's option
 * matching already assumed, preserved deliberately rather than improved, so
 * this extraction changes no wire format.
 *
 * Precedence is values → custom → multi, matching what the two submission sites
 * did. A field cannot legitimately appear in more than one bucket (the editor
 * writes each field to exactly one), so the order is a formality rather than a
 * decision — but it is fixed here so it cannot drift between call sites again.
 */
export function buildCatalogueConfiguration(
  state: CatalogueConfigurationState,
): Record<string, string> {
  return {
    ...state.values,
    ...state.custom,
    ...Object.fromEntries(
      Object.entries(state.multi).map(([key, selected]) => [
        key,
        selected.join(", "),
      ]),
    ),
  };
}

/**
 * The configuration state a wire configuration came from: the inverse of
 * `buildCatalogueConfiguration` over the item's fields, so a configuration
 * the server prefilled (One's item configuration widget) opens as the chips,
 * custom values and multi picks that send it back unchanged.
 *
 * A multi field's value splits on ", "; an always-custom field's value is its
 * custom entry; a choice field's value is its option code, or, for a field
 * that takes its own value (choice_or_custom, a wired stepper) and holds one
 * that is no option's code, `CUSTOM_OPTION_SENTINEL` with the value as its
 * custom entry. A fixed field, or one the configuration leaves out, has none.
 */
export function selectionFromConfiguration(
  fields: readonly CatalogueField[],
  configuration: Readonly<Record<string, string>>,
): CatalogueConfigurationState {
  const state: CatalogueConfigurationState = {
    values: {},
    custom: {},
    multi: {},
  };
  for (const field of fields) {
    const value = configuration[field.key];
    if (value === undefined || field.mode === "fixed") continue;
    if (field.control === "multi") {
      state.multi[field.key] = value ? value.split(", ") : [];
    } else if (field.mode === "always_custom") {
      state.custom[field.key] = value;
    } else if (
      field.mode === "choice_or_custom" &&
      !(field.options ?? []).some((o) => o.code === value)
    ) {
      state.values[field.key] = CUSTOM_OPTION_SENTINEL;
      state.custom[field.key] = value;
    } else {
      state.values[field.key] = value;
    }
  }
  return state;
}
